import { defineStore } from "pinia";
import { ToastBuilder } from "@vcl";
import { useStudioNavigationStore } from "@studio/Stores/studio-navigation-store";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import App from "@bloglovin/inspire-engine/App";
import {
  hasCustomStoryTypeUrlSegment,
  storyTypeUrlTemplateFor,
} from "@studio/Helpers/story-type-url-template";

const FOLDER_ROLE_KEY = "folder";


function reportConfigWriteFailure(label, detail, error, restore) {
  restore();
  console.error(`[studio] ${label} did not save`, { ...detail, error }); // eslint-disable-line no-console
  new ToastBuilder()
      .setTitle("Setting did not save")
      .setText(`${label} could not be saved and has been reverted. ${error?.message ?? ""}`.trim())
      .setToastType(ToastBuilder.TOAST_TYPE_STANDARD)
      .setTimeout(8000)
      .setClosable(true)
      .build();
}

export const useStudioStoryTypePagesStore = defineStore("studio-story-type-pages-store", {
  state: () => ({
    publicationId: 0,
    assignments: {},
    // { <storyType>: [{ id, name, isAssigned }] } — server-listed so the settings
    // page needs no DynamicComponent hydration to show a type's variants.
    storyTypeComponents: {},
    isCreatingComponentVariant: "",
    enabledMap: {},
    urlSegments: {},
    urlEnabledMap: {},
    customPageEnabledMap: {},
    urlSegmentsPublicationId: 0,
    storyTypes: [],
    isCreatingPage: false,
    isCreatingCard: false,
    isSavingUrlSegment: false,
    queuedUrlSegmentSaveActions: [],
    completionTrackingEnabled: false,
    completionSubVariantAssignments: {},
    isCreatingSubVariant: false,
    isEnablingPrograms: false,
    isDisablingPrograms: false,
    showExclusiveStoriesToNonSubscribers: true,
  }),
  getters: {
    cardAssignments() {
      return useCompletionAssignmentStore().storyTypeCardAssignments(this.publicationId);
    },
    completionTwoStateMode() {
      return useCompletionAssignmentStore().isTwoStateMode(this.publicationId);
    },
    pageRoles() {
      const roles = [{ key: FOLDER_ROLE_KEY, label: "Folder" }];
      this.storyTypes.forEach(type => {
        roles.push({ key: type.key, label: type.label });
      });
      return roles;
    },
    cardTypeRoles() {
      return this.storyTypes.map(type => ({ key: type.key, label: type.label }));
    },
    enabledStoryTypes() {
      return this.storyTypes
          .filter(type => this.enabledMap[type.key])
          .map(type => ({ key: type.key, label: type.label }));
    },
    isTypeEnabled() {
      return storyType => Boolean(this.enabledMap[storyType]);
    },
    urlSegmentFor() {
      return storyType => this.urlSegments[storyType] || `${storyType}s`;
    },
    customUrlSegmentFor() {
      return storyType => this.urlSegments[storyType] || "";
    },
    // The template a story type's reader URLs are actually built from — what the
    // settings sidebar and the page inspector's Slug field both mirror.
    storyTypeUrlTemplateFor() {
      return storyType => storyTypeUrlTemplateFor(storyType, this.urlSegments);
    },
    hasCustomStoryTypeUrlSegmentFor() {
      return storyType => hasCustomStoryTypeUrlSegment(storyType, this.urlSegments);
    },
    // Explicit per-type flag; when the backend hasn't stored one it defaults to
    // whether a custom segment exists (the migration-free default the server uses).
    isCustomUrlEnabledFor() {
      return storyType => {
        if (storyType in this.urlEnabledMap) {
          return Boolean(this.urlEnabledMap[storyType]);
        }
        return hasCustomStoryTypeUrlSegment(storyType, this.urlSegments);
      };
    },
    // A type whose definition says it has no reader page (member posts) can
    // never have a custom one — the stored map is not consulted for it.
    supportsCustomPageFor() {
      return storyType => this.storyTypes
          .find(type => type.key === storyType)?.supports_custom_page !== false;
    },
    isCustomPageEnabledFor() {
      return storyType => this.supportsCustomPageFor(storyType)
        && this.customPageEnabledMap[storyType] !== false;
    },
    assignedPageId() {
      return roleKey => this.assignments[roleKey] || 0;
    },
    assignedCardId() {
      return storyType => this.cardAssignments[storyType] || 0;
    },
    componentsForStoryType() {
      return storyType => this.storyTypeComponents[storyType] ?? [];
    },
    storyTypeKeyForComponentName() {
      const normalize = value => String(value || "")
          .toLowerCase()
          .replace(/[_–-]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      return componentName => {
        const normalizedName = normalize(componentName);
        if (!normalizedName) {
          return "";
        }
        const typesByLongestKey = [...this.storyTypes]
            .sort((first, second) => normalize(second.key).length - normalize(first.key).length);
        const match = typesByLongestKey.find(type => {
          const normalizedType = normalize(type.key);
          return normalizedType
            && (normalizedName === normalizedType || normalizedName.startsWith(`${normalizedType} `));
        });
        return match?.key ?? "";
      };
    },
    subVariantAssignedId() {
      return (baseCardDynamicComponentId, status) => {
        return this.completionSubVariantAssignments[`${baseCardDynamicComponentId}:${status}`] || 0;
      };
    },
    completionStatusOptions() {
      if (this.completionTwoStateMode) {
        return [
          { key: "not_started", label: "Not started" },
          { key: "complete", label: "Complete" },
        ];
      }
      return [
        { key: "not_started", label: "Not started" },
        { key: "started", label: "Started" },
        { key: "complete", label: "Complete" },
      ];
    },
  },
  actions: {
    initialize(publicationId) {
      this.publicationId = Number(publicationId) || 0;
      return this.loadStoryTypeConfig(this.publicationId);
    },
    // One request for every story-type + completion setting. The assignment
    // store owns the fetch (and the render-surface payload short-circuit); this
    // copies the slice the editor mutates into local state, so the optimistic
    // writes below keep working unchanged.
    loadStoryTypeConfig(publicationId) {
      const destinationId = Number(publicationId) || 0;
      if (destinationId == 0) {
        return;
      }

      return useCompletionAssignmentStore().ensureStoryTypeConfig(destinationId)
          .then(() => this.applyStoryTypeConfig(destinationId));
    },
    applyStoryTypeConfig(publicationId) {
      const config = useCompletionAssignmentStore().storyTypeConfig(publicationId) ?? {};
      this.storyTypes = Array.isArray(config.storyTypes) ? config.storyTypes : [];
      this.storyTypeComponents = config.components ?? {};
      this.assignments = config.pageAssignments ?? {};
      this.enabledMap = config.enabledMap ?? {};
      this.urlSegments = config.urlSegments ?? {};
      this.urlEnabledMap = config.urlEnabledMap ?? {};
      this.customPageEnabledMap = config.customPageEnabledMap ?? {};
      this.urlSegmentsPublicationId = publicationId;
      this.completionTrackingEnabled = Boolean(config.completionTrackingEnabled);
      this.completionSubVariantAssignments = config.completionSubVariantAssignments ?? {};
      // Absent ⇒ true (backend default); only an explicit false disables it.
      this.showExclusiveStoriesToNonSubscribers = config.showExclusiveStoriesToNonSubscribers !== false;
    },
    // Assign an EXISTING component as a base card's sub-variant for a status;
    // 0 clears the slot (createOrEditCompletionSubVariant CREATES one instead).
    setSubVariantAssignment(baseCardDynamicComponentId, status, dynamicComponentId) {
      const key = `${baseCardDynamicComponentId}:${status}`;
      const previous = this.completionSubVariantAssignments[key];
      this.completionSubVariantAssignments = {
        ...this.completionSubVariantAssignments,
        [key]: dynamicComponentId,
      };
      this.apiPost(`/dynamic-component/${this.publicationId}/completion-sub-variant-assignment`, {
        base_card_dynamic_component_id: baseCardDynamicComponentId,
        completion_status: status,
        dynamic_component_id: dynamicComponentId,
      }, () => {}, error => {
        this.completionSubVariantAssignments = {
          ...this.completionSubVariantAssignments,
          [key]: previous,
        };
        // eslint-disable-next-line no-console
        console.error(
          "setSubVariantAssignment: failed to persist the completion sub-variant assignment; rolled back",
          { publicationId: this.publicationId, baseCardDynamicComponentId, status, dynamicComponentId, error },
        );
      });
    },
    createOrEditCompletionSubVariant(baseCardDynamicComponentId, status) {
      const existingId = this.subVariantAssignedId(baseCardDynamicComponentId, status);
      if (existingId) {
        this.goToPageEditor(existingId);
        return;
      }
      if (this.isCreatingSubVariant) {
        return;
      }
      this.isCreatingSubVariant = true;
      this.apiPost(`/dynamic-component/${this.publicationId}/create-completion-sub-variant`, {
        base_card_dynamic_component_id: baseCardDynamicComponentId,
        completion_status: status,
      }, data => {
        this.isCreatingSubVariant = false;
        if (data?.dynamicComponentId) {
          this.completionSubVariantAssignments = {
            ...this.completionSubVariantAssignments,
            [`${baseCardDynamicComponentId}:${status}`]: data.dynamicComponentId,
          };
          this.goToPageEditor(data.dynamicComponentId);
        }
      }, () => {
        this.isCreatingSubVariant = false;
      });
    },
    setCompletionTrackingEnabled(enabled) {
      this.completionTrackingEnabled = enabled;
      this.apiPost(`/dynamic-component/${this.publicationId}/set-completion-tracking-enabled`, {
        enabled,
      }, () => {}, () => {
        this.completionTrackingEnabled = !enabled;
      });
    },
    setShowExclusiveStoriesToNonSubscribers(enabled) {
      this.showExclusiveStoriesToNonSubscribers = enabled;
      this.apiPost(`/dynamic-component/${this.publicationId}/set-show-exclusive-stories-to-non-subscribers`, {
        enabled,
      }, () => {}, () => {
        this.showExclusiveStoriesToNonSubscribers = !enabled;
      });
    },
    loadCompletionTwoStateMode() {
      return useCompletionAssignmentStore().ensureStoryTypeConfig(this.publicationId);
    },
    setCompletionTwoStateMode(enabled) {
      const assignmentStore = useCompletionAssignmentStore();
      assignmentStore.applyTwoStateMode(this.publicationId, enabled);
      this.apiPost(`/dynamic-component/${this.publicationId}/set-completion-two-state-mode`, {
        enabled,
      }, () => {}, () => {
        assignmentStore.applyTwoStateMode(this.publicationId, !enabled);
      });
    },
    // Re-reads config the SERVER changed behind our back (feature enablement and
    // scaffolding create pages/cards we cannot predict locally).
    refreshStoryTypeConfig() {
      const assignmentStore = useCompletionAssignmentStore();
      return Promise.resolve(assignmentStore.refreshStoryTypeConfig(this.publicationId))
          .then(() => this.applyStoryTypeConfig(this.publicationId));
    },

    // The plus icon on the story-type panel. Reuses the inspector's
    // createDynamicComponentVariant so the new component joins the source's
    // variantGroupId and stays reachable from the inspector's variant arrows —
    // a reimplementation would silently break that navigation.
    async createStoryTypeComponentVariant(storyType) {
      if (this.isCreatingComponentVariant) {
        return 0;
      }
      const sourceId = this.assignedCardId(storyType);
      if (!sourceId) {
        // eslint-disable-next-line no-console
        console.error(
          "createStoryTypeComponentVariant: the story type has no assigned card to copy, so no variant was added",
          { storyType },
        );
        return 0;
      }
      this.isCreatingComponentVariant = storyType;
      try {
        // The settings page loads no DynamicComponent/Element entities, and the
        // variant deep-copies the source's elements — so hydrate first.
        const source = await this.hydrateDynamicComponent(sourceId);
        if (!source) {
          return 0;
        }
        const editor = useDynamicComponentEditorStore();
        const newId = await editor.createDynamicComponentVariant(sourceId);
        if (!newId) {
          return 0;
        }
        const variantNumber = this.componentsForStoryType(storyType).length + 1;
        App.get("CreatorStudio").triggerEvent("UpdateDynamicComponentName", {
          dynamicComponent: { id: newId },
          name: `story type ${storyType} variant ${variantNumber}`,
        });
        // createDynamicComponentVariant returns the client's OPTIMISTIC pk and the
        // row lands a moment later, so a single reload races it — poll until the
        // server actually lists the new component.
        this.reloadStoryTypeComponentsUntilGrown(storyType, this.componentsForStoryType(storyType).length);
        return newId;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "createStoryTypeComponentVariant: variant creation failed — no component was added",
          { storyType, sourceId, error },
        );
        return 0;
      } finally {
        this.isCreatingComponentVariant = "";
      }
    },
    reloadStoryTypeComponentsUntilGrown(storyType, previousCount, attempt = 0) {
      this.refreshStoryTypeConfig();
      if (this.componentsForStoryType(storyType).length > previousCount) {
        return;
      }
      if (attempt >= 20) {
        // eslint-disable-next-line no-console
        console.error(
          "reloadStoryTypeComponentsUntilGrown: the new variant never appeared in the list — reload the page to see it",
          { storyType, previousCount },
        );
        return;
      }
      setTimeout(
        () => this.reloadStoryTypeComponentsUntilGrown(storyType, previousCount, attempt + 1),
        500,
      );
    },
    // Resolves once the DC AND its elements are readable, since the variant copy
    // walks the element tree. Bounded so a never-hydrating id fails loudly.
    hydrateDynamicComponent(dynamicComponentId) {
      return new Promise(resolve => {
        const studio = App.get("CreatorStudio");
        let attempts = 0;
        const poll = () => {
          const dc = DynamicComponent.get(dynamicComponentId);
          const rootElementId = dc?.rootElementId;
          const elementsLoaded = rootElementId
            && Boolean(studio.getEntity("Element", { id: String(rootElementId) }));
          if (dc?.isHydrated && elementsLoaded) {
            resolve(dc);
            return;
          }
          attempts += 1;
          if (attempts >= 60) {
            // eslint-disable-next-line no-console
            console.error(
              "hydrateDynamicComponent: component never hydrated — variant creation aborted",
              { dynamicComponentId },
            );
            resolve(null);
            return;
          }
          setTimeout(poll, 250);
        };
        poll();
      });
    },
    setStoryTypeEnabled(storyType, enabled, label = storyType) {
      this.enabledMap = { ...this.enabledMap, [storyType]: enabled };
      this.apiPost(`/dynamic-component/${this.publicationId}/set-story-type-enabled`, {
        story_type: storyType,
        enabled,
      }, () => {
        if (enabled) {
          this.ensureStoryTypeScaffold(storyType, label);
        }
      }, () => {
        this.enabledMap = { ...this.enabledMap, [storyType]: !enabled };
      });
    },
    // The page inspector needs the segment map to mirror a story-type page's URL
    // but must not claim `publicationId` — StoryComponentPickerModal keys its own
    // initialize() guard off that field, and setting it here would skip the
    // picker's load of card assignments entirely.
    ensureStoryTypeUrlSegmentsLoaded(publicationId) {
      const destinationId = Number(publicationId) || 0;
      if (destinationId == 0 || this.urlSegmentsPublicationId == destinationId) {
        return;
      }
      return this.loadStoryTypeConfig(destinationId);
    },
    // Mirror the page's `story` uri binding into publication configuration. The
    // READER resolves URLs from config — it cannot read the page's rootOptions,
    // which live on a builder entity an anonymous visitor has no access to. Pass
    // an empty urlVar to clear the binding.
    //
    // Takes the publication id rather than reading state: the page editor
    // deliberately never claims `publicationId` (StoryComponentPickerModal keys its
    // own load guard off it), so it is 0 on the surface that calls this.
    setStoryTypeUrlBinding(publicationId, storyType, urlVar, resolveBy) {
      this.apiPost(`/dynamic-component/${publicationId}/set-story-type-url-binding`, {
        story_type: storyType,
        url_var: urlVar,
        resolve_by: resolveBy,
      }, () => {}, error => {
        // eslint-disable-next-line no-console
        console.error(
          "setStoryTypeUrlBinding: the page's story binding did not reach publication config, "
            + "so the reader will not resolve this type's URLs",
          { publicationId, storyType, urlVar, resolveBy, error },
        );
      });
    },
    enablePrograms(onSuccess = () => {}, onError = () => {}) {
      this.isEnablingPrograms = true;
      this.apiPost(`/dynamic-component/${this.publicationId}/enable-programs`, {}, () => {
        this.isEnablingPrograms = false;
        this.refreshStoryTypeConfig();
        onSuccess();
      }, error => {
        this.isEnablingPrograms = false;
        onError(error);
      });
    },
    // Non-destructive server-side disable of every program story type; refresh
    // so the client enabledMap reflects the flip.
    disablePrograms(onSuccess = () => {}, onError = () => {}) {
      this.isDisablingPrograms = true;
      this.apiPost(`/dynamic-component/${this.publicationId}/disable-programs`, {}, () => {
        this.isDisablingPrograms = false;
        this.refreshStoryTypeConfig();
        onSuccess();
      }, error => {
        this.isDisablingPrograms = false;
        onError(error);
      });
    },
    setStoryTypeUrlSegment(storyType, urlSegment) {
      const previous = this.urlSegments[storyType];
      this.urlSegments = { ...this.urlSegments, [storyType]: urlSegment };
      this.isSavingUrlSegment = true;
      this.apiPost(`/dynamic-component/${this.publicationId}/set-story-type-url-segment`, {
        story_type: storyType,
        url_segment: urlSegment,
      }, () => {}, error => {
        reportConfigWriteFailure(
          "Custom URL segment",
          { publicationId: this.publicationId, storyType, urlSegment },
          error,
          () => {
            this.urlSegments = { ...this.urlSegments, [storyType]: previous };
          },
        );
      }, () => {
        this.isSavingUrlSegment = false;
        const queuedActions = this.queuedUrlSegmentSaveActions;
        this.queuedUrlSegmentSaveActions = [];
        queuedActions.forEach(action => action());
      });
    },
    setStoryTypeUrlEnabled(storyType, enabled) {
      const previous = this.urlEnabledMap[storyType];
      this.urlEnabledMap = { ...this.urlEnabledMap, [storyType]: enabled };
      this.apiPost(`/dynamic-component/${this.publicationId}/set-story-type-url-enabled`, {
        story_type: storyType,
        enabled,
      }, () => {}, error => {
        reportConfigWriteFailure(
          "Custom URL",
          { publicationId: this.publicationId, storyType, enabled },
          error,
          () => {
            this.urlEnabledMap = { ...this.urlEnabledMap, [storyType]: previous };
          },
        );
      });
    },
    setStoryTypeCustomPageEnabled(storyType, enabled) {
      const previous = this.customPageEnabledMap[storyType];
      this.customPageEnabledMap = { ...this.customPageEnabledMap, [storyType]: enabled };
      this.apiPost(`/dynamic-component/${this.publicationId}/set-story-type-custom-page-enabled`, {
        story_type: storyType,
        enabled,
      }, () => {}, error => {
        reportConfigWriteFailure(
          "Custom page",
          { publicationId: this.publicationId, storyType, enabled },
          error,
          () => {
            this.customPageEnabledMap = { ...this.customPageEnabledMap, [storyType]: previous };
          },
        );
      });
    },
    runAfterUrlSegmentSave(action) {
      if (!this.isSavingUrlSegment) {
        action();
        return;
      }
      this.queuedUrlSegmentSaveActions = [...this.queuedUrlSegmentSaveActions, action];
    },
    designPageForRole(roleKey, name, description) {
      if (this.isCreatingPage) {
        return;
      }
      const existingPageId = this.assignments[roleKey];
      if (existingPageId) {
        this.goToPageEditor(existingPageId);
        return;
      }
      this.isCreatingPage = true;
      this.runAfterUrlSegmentSave(() => {
        this.apiPost(`/dynamic-component/${this.publicationId}/create-story-type-page`, {
          page_role_key: roleKey,
          name,
          description,
        }, data => {
          this.isCreatingPage = false;
          if (data?.dynamicComponentId) {
            this.assignments = { ...this.assignments, [roleKey]: data.dynamicComponentId };
            this.goToPageEditor(data.dynamicComponentId);
          }
        }, () => {
          this.isCreatingPage = false;
        });
      });
    },
    goToPageEditor(dynamicComponentId) {
      useStudioNavigationStore().navigateToUri(`/build/${this.publicationId}/pages/${dynamicComponentId}`);
    },
    goToComponentEditor(dynamicComponentId) {
      useStudioNavigationStore().navigateToUri(`/build/${this.publicationId}/components/${dynamicComponentId}`);
    },
    // Assign an EXISTING dynamic component as the card for a story type (the
    // FeedV2 Story Component Picker path — as opposed to designCardForType,
    // which CREATES a new card and opens its editor). Optimistic; rolls back
    // on failure. Persists to the same publication-level storyTypeCardAssignments
    // map, so the assignment is shared with Features → Stories.
    setCardAssignment(storyType, dynamicComponentId) {
      const assignmentStore = useCompletionAssignmentStore();
      const previous = this.cardAssignments[storyType];
      assignmentStore.applyStoryTypeCardAssignment(this.publicationId, storyType, dynamicComponentId);
      this.apiPost(`/dynamic-component/${this.publicationId}/story-type-card-assignment`, {
        story_type: storyType,
        dynamic_component_id: dynamicComponentId,
      }, () => {}, error => {
        assignmentStore.applyStoryTypeCardAssignment(this.publicationId, storyType, previous);
        // eslint-disable-next-line no-console
        console.error(
          "setCardAssignment: failed to persist the story-type card assignment; rolled back",
          { publicationId: this.publicationId, storyType, dynamicComponentId, error },
        );
      });
    },
    // Toggling a type on scaffolds its page and first card WITHOUT navigating:
    // designPageForRole/designCardForType open the editor, which would throw the
    // author out of the settings sidebar mid-toggle.
    ensureStoryTypeScaffold(storyType, label) {
      this.runAfterUrlSegmentSave(() => {
        if (this.supportsCustomPageFor(storyType) && !this.assignments[storyType]) {
          this.apiPost(`/dynamic-component/${this.publicationId}/create-story-type-page`, {
            page_role_key: storyType,
            name: `${label} Page`,
            description: "",
          }, data => {
            if (data?.dynamicComponentId) {
              this.assignments = { ...this.assignments, [storyType]: data.dynamicComponentId };
            }
          }, error => {
            // eslint-disable-next-line no-console
            console.error(
              `ensureStoryTypeScaffold: no page was created for "${storyType}" — it will show as missing`,
              { publicationId: this.publicationId, error },
            );
          });
        }
        if (!this.cardAssignments[storyType]) {
          this.apiPost(`/dynamic-component/${this.publicationId}/create-story-type-card`, {
            story_type: storyType,
          }, data => {
            if (data?.dynamicComponentId) {
              useCompletionAssignmentStore()
                  .applyStoryTypeCardAssignment(this.publicationId, storyType, data.dynamicComponentId);
              this.refreshStoryTypeConfig();
            }
          }, error => {
            // eslint-disable-next-line no-console
            console.error(
              `ensureStoryTypeScaffold: no component was created for "${storyType}"`,
              { publicationId: this.publicationId, error },
            );
          });
        }
      });
    },
    designCardForType(storyType) {
      if (this.isCreatingCard) {
        return;
      }
      const existingCardId = this.cardAssignments[storyType];
      if (existingCardId) {
        this.goToPageEditor(existingCardId);
        return;
      }
      this.isCreatingCard = true;
      this.runAfterUrlSegmentSave(() => {
        this.apiPost(`/dynamic-component/${this.publicationId}/create-story-type-card`, {
          story_type: storyType,
        }, data => {
          this.isCreatingCard = false;
          if (data?.dynamicComponentId) {
            useCompletionAssignmentStore()
                .applyStoryTypeCardAssignment(this.publicationId, storyType, data.dynamicComponentId);
            this.goToPageEditor(data.dynamicComponentId);
          }
        }, () => {
          this.isCreatingCard = false;
        });
      });
    },
  },
});
